import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import {
    formatBoardDuplicateSelectionMessage,
    formatBoardDuplicateWithFirstBoardMessage,
} from '../constants/client-creation.constants'
import { IAmountValidationResult } from '../models/validation.model'

export function roundMoneyParsedNumber(value: number): number {
    if (!Number.isFinite(value)) return value
    return Math.round(value * 100) / 100
}

export function validateAmountInput(value: string): IAmountValidationResult {
    const cleaned = value
        .replace(REGEX_PATTERNS.THOUSANDS_SEPARATOR_COMMA_GLOBAL, '')
        .replace(REGEX_PATTERNS.NON_NUMERIC_DECIMAL, '')

    if (!cleaned) return { normalized: '', isValid: false }

    const decimalParts = cleaned.split('.')
    if (decimalParts.length > 2) {
        return { normalized: cleaned, isValid: false }
    }

    const integerPart = decimalParts[0] || ''
    const decimalPart = decimalParts[1] ?? ''
    const hasTooManyIntegerDigits = integerPart.length > 8
    const hasTooManyDecimals = decimalPart.length > 2
    const isNumeric = REGEX_PATTERNS.AMOUNT_UPTO_TWO_DECIMALS.test(cleaned)

    if (hasTooManyIntegerDigits || hasTooManyDecimals || !isNumeric) {
        return { normalized: cleaned, isValid: false }
    }

    return { normalized: cleaned, isValid: true }
}

export function formatCurrencyForTyping(
    value: string,
    formatIndianIntegerDigits: (integerDigits: string) => string,
): string {
    if (!value) return ''

    const [integerPartRaw, decimalPartRaw] = value.split('.')
    let integerDigits = (integerPartRaw || '0').replace(REGEX_PATTERNS.NON_NUMERIC, '')
    integerDigits =
        integerDigits.replace(REGEX_PATTERNS.NUMERIC_LEADING_ZEROS_BEFORE_DIGIT, '') || '0'

    const formattedInteger = formatIndianIntegerDigits(integerDigits)
    if (!formattedInteger) return ''

    const decimals =
        decimalPartRaw !== undefined
            ? decimalPartRaw.replace(REGEX_PATTERNS.NON_NUMERIC, '').slice(0, 2)
            : ''
    if (!decimals) {
        return `₹ ${formattedInteger}`
    }

    return `₹ ${formattedInteger}.${decimals}`
}

export function parseAmountToNumber(value: unknown): number {
    const normalized = String(value ?? '')
        .replace(REGEX_PATTERNS.THOUSANDS_SEPARATOR_COMMA_GLOBAL, '')
        .replace(REGEX_PATTERNS.NON_NUMERIC_DECIMAL, '')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? roundMoneyParsedNumber(parsed) : 0
}

export function selectedGradesTextFromValue(value: unknown): string {
    const gradeNumbers = Array.isArray(value)
        ? value
              .map((gradeValue) => Number(gradeValue))
              .filter((gradeValue) => Number.isFinite(gradeValue))
              .sort((leftGrade, rightGrade) => leftGrade - rightGrade)
        : []
    if (!gradeNumbers.length) return ''
    return gradeNumbers.join(', ')
}

export function buildBoardCardSubtitle(boardName: string, gradesCommaSeparated: string): string {
    const trimmedBoard = boardName.trim()
    if (trimmedBoard && gradesCommaSeparated) {
        return `${trimmedBoard} | Grades ${gradesCommaSeparated}`
    }
    if (trimmedBoard) {
        return trimmedBoard
    }
    if (gradesCommaSeparated) {
        return `Grades ${gradesCommaSeparated}`
    }
    return 'No board or grades selected'
}

export function isDuplicateBoardAtIndex(
    boardRawValues: readonly string[],
    cardIndex: number,
): boolean {
    const currentBoard = String(boardRawValues[cardIndex] ?? '')
        .trim()
        .toLowerCase()
    if (!currentBoard) return false

    const firstIndex = boardRawValues.findIndex(
        (value) =>
            String(value ?? '')
                .trim()
                .toLowerCase() === currentBoard,
    )
    // Only later cards are "duplicates"; the first card with this board stays valid (NAV / UX).
    return firstIndex !== -1 && firstIndex < cardIndex
}

export function boardDuplicateSelectionMessage(displayBoardName: string): string {
    return formatBoardDuplicateSelectionMessage(displayBoardName)
}

/** Message for the duplicate card only; names the first card that already uses this board. */
export function boardDuplicateSelectionMessageWithFirstCard(
    boardRawValues: readonly string[],
    duplicateCardIndex: number,
): string {
    const boardName = String(boardRawValues[duplicateCardIndex] ?? '').trim()
    if (!boardName) return ''
    const normalizedKey = boardName.toLowerCase()
    const firstIndex = boardRawValues.findIndex(
        (boardValue) =>
            String(boardValue ?? '')
                .trim()
                .toLowerCase() === normalizedKey,
    )
    if (firstIndex < 0 || firstIndex === duplicateCardIndex) {
        return formatBoardDuplicateSelectionMessage(boardName)
    }
    return formatBoardDuplicateWithFirstBoardMessage(boardName, firstIndex + 1)
}

export function hasStudentStrengthInputError(
    rawValue: string,
    studentStrengthMax: number,
): boolean {
    const trimmed = rawValue.trim()
    if (!trimmed) return false
    const normalizedStrength = trimmed
        .replace(REGEX_PATTERNS.THOUSANDS_SEPARATOR_COMMA_GLOBAL, '')
        .trim()
    if (!normalizedStrength || !REGEX_PATTERNS.DIGITS_ONLY.test(normalizedStrength)) return true

    try {
        return BigInt(normalizedStrength) > BigInt(studentStrengthMax)
    } catch {
        return true
    }
}
